import { VideoOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Pill, SectionBreakCurve } from '@/components/ui';
import VideoPlayer from '@/components/ui/video-player';
import {
  OnDemandTopic,
  RecordableItem,
  Session,
  SessionAttendance,
  Topic,
} from '@/features/modules/types';
import { useGetVideoSegmentationData } from '@/features/user/api/queries';
import { useAnalytics } from '@/lib/analytics';
import { useModuleNoun } from '@/lib/feature-flags';
import { getSessionLabel } from '@/lib/utils';

import Transcripts from './transcripts';

const SESSION_LABEL = getSessionLabel();

type RecordingItemProps = {
  videoUrl: string;
  videoTitle: string;
  sessionNumber?: number;
  attendance?: boolean;
  sessionId?: string;
  moduleNumber?: string;
  bannerUrl?: string;
  sessionStartTime?: string;
  topicName?: string;
};

const makeAttendance = (
  attendance?: SessionAttendance
): boolean | undefined => {
  if (attendance?.status === 'ATTENDED') {
    return true;
  }
  if (attendance?.status === 'MISSED') {
    return false;
  }
  return undefined;
};

const getTopicNames = (topics: Topic[]): string => {
  try {
    return topics?.map(topic => topic?.name ?? '').join(', ');
  } catch {
    return '';
  }
};

const getSessionStartTime = (session: Session): string => {
  try {
    return session?.date?.timestamp?.toString() ?? '';
  } catch {
    return '';
  }
};

// Unified factory function
export const makeRecordingItemProps = (
  item: Session | OnDemandTopic | RecordableItem
): RecordingItemProps => {
  // If it's already a RecordableItem, convert directly
  if ('type' in item && (item.type === 'session' || item.type === 'topic')) {
    const recordable = item as RecordableItem;
    return {
      videoUrl: recordable.videoUrl,
      videoTitle: recordable.name,
      sessionNumber:
        recordable.type === 'session' ? recordable.sessionNumber : undefined,
      attendance:
        recordable.type === 'session' ? recordable.attendance : undefined,
      sessionId: recordable.transcriptSessionId,
      moduleNumber:
        recordable.type === 'topic' ? recordable.moduleNumber : undefined,
      bannerUrl:
        recordable.type === 'session' ? recordable.bannerUrl : undefined,
    };
  }

  // If it's a Session (has 'video' property)
  if ('video' in item) {
    const session = item;
    return {
      videoUrl: session?.video?.url ?? session?.video?.play_url ?? '',
      videoTitle: session?.name ?? '',
      sessionNumber: session?.number,
      attendance: makeAttendance(session?.attendance),
      sessionId: session.id,
      sessionStartTime: getSessionStartTime(session),
      topicName: getTopicNames(session?.topics ?? []),
    };
  }

  // Otherwise it's an OnDemandTopic
  const topic = item;
  return {
    videoUrl: topic?.recording?.manifest_path ?? '',
    videoTitle: topic.name,
    moduleNumber: topic.module_id,
  };
};

const RecordingNotAvailable = () => {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-magical-moonlight-white bg-gradient-to-br from-magical-moonlight-d2 to-magical-moonlight-d3 p-8">
      <div className="bg-magical-moonlight-white/10 rounded-full p-6 mb-4">
        <VideoOff className="w-12 h-12 text-magical-moonlight-white/60" />
      </div>
      <p className="text-lg font-semibold text-magical-moonlight-white mb-2">
        No Recording Available
      </p>
      <p className="text-sm text-magical-moonlight-white/70 text-center max-w-sm">
        The recording for this session is not available yet. Please check back
        later.
      </p>
    </div>
  );
};

const RecordingItem = ({
  videoUrl,
  videoTitle,
  sessionNumber,
  attendance,
  moduleNumber,
  sessionId,
  bannerUrl,
  topicName,
  sessionStartTime,
}: RecordingItemProps) => {
  const [jumpToTime, setJumpToTime] = useState<number | undefined>();
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const currentVideoTimeRef = useRef<number>(0);
  const { trackEvent } = useAnalytics();
  const noun = useModuleNoun();

  // Reuses the same cached query as the Transcripts panel (deduped by React
  // Query), surfacing the segments as chapter markers on the seekbar.
  const { segments } = useGetVideoSegmentationData(sessionId ?? '');
  const chapters = useMemo(
    () => segments.map(s => ({ time: s.start_time, title: s.title })),
    [segments]
  );

  // Track recording opened on mount
  useEffect(() => {
    trackEvent('recordingOpened', 1, {
      recordingId: sessionId,
      recordingTitle: videoTitle,
    });
  }, [trackEvent, sessionId, videoTitle]);

  // Track video play started (first play only)
  const handleVideoPlay = () => {
    if (!hasPlayedOnce) {
      trackEvent('videoPlayStarted', 1, {
        recordingId: sessionId,
        recordingTitle: videoTitle,
        topicName,
        sessionStartTime,
      });
      setHasPlayedOnce(true);
    }
  };

  // Update current video time for segment skip tracking
  // Using ref to avoid re-renders on every time update
  const handleTimeUpdate = (currentTime: number) => {
    currentVideoTimeRef.current = currentTime;
  };

  return (
    <div className="w-full flex flex-col overflow-y-auto">
      {/* Header with pills */}
      <div className="py-6 pb-4 px-6">
        <div className="flex flex-wrap gap-2 items-center mb-4">
          {moduleNumber ? (
            <Pill
              rounded
              variant="outline"
              size="sm"
              className="bg-gray-100 text-gray-700 border-gray-300 px-3 py-1"
            >
              {noun.singular} {moduleNumber}
            </Pill>
          ) : null}
          {sessionNumber ? (
            <Pill
              rounded
              variant="outline"
              size="sm"
              className="bg-gray-100 text-gray-700 border-gray-300 px-3 py-1"
            >
              {SESSION_LABEL.capitalize} {sessionNumber}
            </Pill>
          ) : null}

          {attendance === undefined ? null : (
            <Pill
              rounded
              variant={attendance ? 'success' : 'error'}
              size="sm"
              className="px-3 py-1"
            >
              {attendance ? 'Attended' : 'Missed'}
            </Pill>
          )}
        </div>

        {/* Lesson Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{videoTitle}</h1>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex flex-col-reverse lg:flex-row gap-6 px-6 pb-6 justify-center">
        {sessionId ? (
          /* Left Panel - Time Stamps */
          <Transcripts
            sessionId={sessionId}
            handleJumpToTime={setJumpToTime}
            getCurrentVideoTime={() => currentVideoTimeRef.current}
          />
        ) : null}

        {/* Right Panel - Video Player */}
        <div className="bg-magical-moonlight-d2 rounded-2xl overflow-hidden h-96 relative flex-1 max-w-4xl">
          {videoUrl ? (
            <VideoPlayer
              src={videoUrl}
              className="w-full h-full"
              poster={bannerUrl}
              jumpTo={jumpToTime}
              chapters={chapters}
              onPlay={handleVideoPlay}
              onTimeUpdate={handleTimeUpdate}
            />
          ) : (
            <RecordingNotAvailable />
          )}
        </div>
      </div>

      <SectionBreakCurve
        className="my-6"
        strokeColor="var(--color-magical-moonlight-d1)"
        bottomColor="transparent"
        topColor="transparent"
        strokeWidth={2}
      />
    </div>
  );
};

export default RecordingItem;
